import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import { articleBodies, newsList } from '@/mocks/news';

type BodyBlock = { type: 'p' | 'h2' | 'quote'; text: string };

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = newsList.find((item) => item.id === id);

  if (!article) {
    return (
      <>
        <Seo
          title="内容不存在 | 黑料爆料"
          description="你访问的内容可能已被合并或更正，请返回首页查看最新的实时情报流。"
          keywords="黑料爆料,内容不存在"
          path="/article"
        />
        <section className="mx-auto flex w-full flex-col items-center justify-center px-4 py-28 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-6 font-heading text-3xl text-foreground-950">没有找到这条内容</h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            该条目可能已被合并进更新的时间线。返回首页即可查看最新的实时情报流。
          </p>
          <Link
            to="/"
            className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </section>
      </>
    );
  }

  const body: BodyBlock[] =
    articleBodies[article.id] ??
    [
      { type: 'p', text: article.excerpt },
      {
        type: 'h2',
        text: '目前已知的公开信息',
      },
      {
        type: 'p',
        text: '这条内容目前可核对的公开节点仍有限，我们已按出现时间整理出可见顺序，并将持续补充后续进展。若有新的公开说明出现，会直接更新在本条目内。',
      },
      { type: 'h2', text: '接下来值得关注的信号' },
      {
        type: 'p',
        text: '一是当事方是否给出正式说明，二是相关合作方是否出现同步调整，三是主流讨论是否从猜测转向对事实的确认。以上任一信号出现，我们都会更新本条状态。',
      },
      {
        type: 'quote',
        text: '本站仅整理公开可见内容，不对事件本身作出判断，配图均为抽象氛围图。',
      },
    ];

  const related = newsList.filter((item) => item.categoryId === article.categoryId && item.id !== article.id);
  const relatedList = (related.length >= 3 ? related : newsList.filter((item) => item.id !== article.id)).slice(0, 3);
  const currentIndex = newsList.findIndex((item) => item.id === article.id);
  const prev = currentIndex > 0 ? newsList[currentIndex - 1] : null;
  const next = currentIndex < newsList.length - 1 ? newsList[currentIndex + 1] : null;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      dateModified: article.date,
      articleSection: article.categoryName,
      author: { '@type': 'Organization', name: '黑料爆料' },
      publisher: { '@type': 'Organization', name: '黑料爆料' },
      mainEntityOfPage: `https://utuyxa.cn/article/${article.id}`,
      keywords: article.tags.join(','),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://utuyxa.cn/' },
        {
          '@type': 'ListItem',
          position: 2,
          name: article.categoryName,
          item: `https://utuyxa.cn/?cat=${article.categoryId}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
          item: `https://utuyxa.cn/article/${article.id}`,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title} | 黑料爆料`}
        description={article.excerpt.slice(0, 110)}
        keywords={`${article.tags.join(',')},黑料爆料,黑料爆料今日,黑料爆料实时更新`}
        path={`/article/${article.id}`}
        type="article"
        jsonLd={jsonLd}
      />

      <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-400">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/?cat=${article.categoryId}#feed`} className="transition-colors hover:text-primary-400">
            {article.categoryName}
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="line-clamp-1 max-w-[220px] text-foreground-700">{article.title}</span>
        </nav>

        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <article className="lg:col-span-8">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-primary-500/10 px-3 py-1.5 text-xs font-label text-primary-400">
              <i className="ri-price-tag-3-line" />
              {article.categoryName}
            </span>
            <h1 className="mt-5 font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {article.date}
              </span>
              <span className="flex items-center gap-1.5 text-accent-500">
                <i className="ri-fire-line" />
                热度 {(article.heat / 10000).toFixed(1)}万
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-chat-3-line" />
                {article.comments} 条讨论
              </span>
            </div>

            <figure className="mt-8 overflow-hidden rounded-lg border border-background-200">
              <div className="h-56 w-full md:h-80">
                <img
                  src={article.cover}
                  alt={`${article.title} 抽象配图`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="bg-background-100 px-5 py-3 text-xs text-foreground-500">
                <i className="ri-image-line mr-1.5" />
                站内配图为抽象氛围图，与事件现场无关。
              </figcaption>
            </figure>

            <div className="mt-9 flex flex-col gap-5">
              {body.map((block, index) => {
                if (block.type === 'h2') {
                  return (
                    <h2
                      key={`h-${index}`}
                      className="mt-3 flex items-center gap-3 font-heading text-2xl text-foreground-950"
                    >
                      <span className="h-5 w-1 rounded-full bg-primary-500" />
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'quote') {
                  return (
                    <blockquote
                      key={`q-${index}`}
                      className="rounded-lg border-l-2 border-accent-500 bg-background-100 px-5 py-4 text-sm leading-relaxed text-foreground-600"
                    >
                      {block.text}
                    </blockquote>
                  );
                }
                return (
                  <p key={`p-${index}`} className="text-[15px] leading-8 text-foreground-700">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/?q=${encodeURIComponent(tag)}#feed`}
                  className="rounded-full border border-secondary-200/60 bg-secondary-100/70 px-3 py-1.5 text-xs font-label text-secondary-400 transition-colors hover:border-secondary-400"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-9 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex items-start gap-3 text-xs leading-relaxed text-foreground-500">
                <i className="ri-error-warning-line mt-0.5 text-base text-accent-400" />
                本文内容为公开信息整理，不代表任何一方立场。若内容与后续公开证据不符，我们将在原文顶部追加更正说明。
              </p>
              <Link
                to="/about#submit"
                className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-xs font-label text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-400"
              >
                <i className="ri-flag-line" />
                补充线索
              </Link>
            </div>

            <nav aria-label="上下篇" className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {prev ? (
                <Link
                  to={`/article/${prev.id}`}
                  className="group rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-400/60"
                >
                  <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-arrow-left-line" />
                    上一条
                  </span>
                  <span className="mt-2 line-clamp-2 block text-sm text-foreground-800 transition-colors group-hover:text-primary-400">
                    {prev.title}
                  </span>
                </Link>
              ) : (
                <span className="hidden sm:block" />
              )}
              {next ? (
                <Link
                  to={`/article/${next.id}`}
                  className="group rounded-lg border border-background-200 bg-background-100 p-4 text-right transition-colors hover:border-primary-400/60"
                >
                  <span className="flex items-center justify-end gap-1.5 text-xs text-foreground-500">
                    下一条
                    <i className="ri-arrow-right-line" />
                  </span>
                  <span className="mt-2 line-clamp-2 block text-sm text-foreground-800 transition-colors group-hover:text-primary-400">
                    {next.title}
                  </span>
                </Link>
              ) : null}
            </nav>
          </article>

          <aside className="lg:col-span-4" aria-label="相关推荐">
            <div className="lg:sticky lg:top-20 flex flex-col gap-4">
              <section className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                  <i className="ri-links-line text-accent-400" />
                  相关推荐
                </h2>
                <ul className="mt-4 flex flex-col gap-4">
                  {relatedList.map((item) => (
                    <li key={item.id}>
                      <Link to={`/article/${item.id}`} className="group flex gap-3">
                        <span className="h-16 w-20 shrink-0 overflow-hidden rounded-md">
                          <img
                            src={item.cover}
                            alt={`${item.title} 配图`}
                            className="h-full w-full object-cover object-top"
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="line-clamp-2 block text-sm text-foreground-800 transition-colors group-hover:text-primary-400">
                            {item.title}
                          </span>
                          <span className="mt-1.5 block text-xs text-foreground-500">{item.time}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="rounded-lg border border-background-200 bg-primary-900/40 p-5">
                <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                  <i className="ri-radar-line text-primary-400" />
                  查看完整情报流
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  首页实时列表按热度与时间双重排序，切换圈子即可只看你关心的方向。
                </p>
                <Link
                  to="/#feed"
                  className="mt-4 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-arrow-right-up-line" />
                  回到今日新瓜
                </Link>
              </section>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}